import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { Loader, PageHeader } from "@/components/common";
import { useAuth } from "@/context/AuthContext";

const RGB = "var(--glow-admin)";
const PLANS = ["essentials", "family_plus", "household_pro"];
const dollars = (c) => (c / 100).toFixed(2);
const cents = (v) => Math.round(parseFloat(v) * 100);

export default function AdminPlans() {
  const { session } = useAuth();
  const isSuper = session?.user?.platform_role === "super_admin";
  const [d, setD] = useState(null);
  const [f, setF] = useState(null);
  const [busy, setBusy] = useState(false);
  const fill = (data) => {
    setD(data);
    setF({ prices: Object.fromEntries(PLANS.map((p) => [p, { month: dollars(data.prices[p].month), year: dollars(data.prices[p].year) }])),
      storage_gb: { ...data.storage_gb }, meters: { ...data.meters }, roommate: dollars(data.roommate_cents), cap: data.roommate_cap_seats, capc: dollars(data.roommate_cap_cents),
      f_on: data.founding.on, f_cap: data.founding.cap });
  };
  useEffect(() => { api.get("/admin/plans").then(({ data }) => fill(data)).catch((e) => toast.error(errMsg(e))); }, []);
  if (!f) return <Loader />;
  const set = (path, v) => setF((o) => { const n = structuredClone(o); let x = n; path.slice(0, -1).forEach((k) => { x = x[k]; }); x[path.at(-1)] = v; return n; });
  const save = async () => {
    setBusy(true);
    try {
      await api.put("/admin/plans", { prices: Object.fromEntries(PLANS.map((p) => [p, { month: cents(f.prices[p].month), year: cents(f.prices[p].year) }])),
        storage_gb: Object.fromEntries(Object.entries(f.storage_gb).map(([k, v]) => [k, parseInt(v, 10)])),
        meters: Object.fromEntries(Object.entries(f.meters).map(([k, v]) => [k, parseInt(v, 10)])), roommate_cents: cents(f.roommate), roommate_cap_seats: parseInt(f.cap, 10), roommate_cap_cents: cents(f.capc) });
      await api.put("/admin/plans/founding", { on: f.f_on, cap: parseInt(f.f_cap, 10) });
      fill((await api.get("/admin/plans")).data);
      toast.success("Plans saved. New prices apply to new subscribers only.");
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const inp = (path, testid, w = "w-24") => {
    const v = path.reduce((x, k) => x[k], f);
    return <input className={`q-input ${w}`} value={v} disabled={!isSuper} onChange={(e) => set(path, e.target.value)} data-testid={testid} />;
  };
  const fc = d.founding;
  return (
    <div data-testid="admin-plans-page">
      <PageHeader eyebrow="Admin" title="Plans" rgb={RGB} testid="admin-plans-title" />
      <section className="q-card overflow-x-auto">
        <table className="w-full text-sm" data-testid="admin-plans-table">
          <thead><tr className="text-left text-xs uppercase tracking-[0.12em] text-q-faint"><th className="py-2">Plan</th><th>$/month</th><th>$/year</th><th>Storage GB</th></tr></thead>
          <tbody>
            {PLANS.map((p) => (
              <tr key={p} className="border-t border-q-line"><td className="py-2 font-semibold text-q-text">{d.names[p]}</td>
                <td>{inp(["prices", p, "month"], `admin-plan-${p}-month`)}</td><td>{inp(["prices", p, "year"], `admin-plan-${p}-year`)}</td><td>{inp(["storage_gb", p], `admin-plan-${p}-storage`, "w-20")}</td></tr>
            ))}
            <tr className="border-t border-q-line"><td className="py-2 font-semibold text-q-text">Roommates</td>
              <td colSpan={2} className="text-q-muted">$ {inp(["roommate"], "admin-roommate-price")} per roommate, capped at $ {inp(["capc"], "admin-roommate-cap-price")} from {inp(["cap"], "admin-roommate-cap", "w-16")} roommates</td>
              <td>{inp(["storage_gb", "roommates"], "admin-plan-roommates-storage", "w-20")}</td></tr>
          </tbody>
        </table>
      </section>
      <section className="q-card mt-6" data-testid="admin-meters">
        <h2 className="font-display text-lg font-bold text-q-text">Fair-use limits (Essentials and Roommates, per hub per month)</h2>
        <div className="mt-3 flex flex-wrap gap-4">
          {[["copilot", "Copilot messages"], ["scans", "AI scans"], ["stories", "New AI stories"], ["voice", "Voice commands"]].map(([k, l]) => (
            <label key={k} className="text-xs text-q-muted">{l}<div className="mt-1">{inp(["meters", k], `admin-meter-${k}`)}</div></label>))}
        </div>
      </section>
      <section className="q-card mt-6" data-testid="admin-founding">
        <h2 className="font-display text-lg font-bold text-q-text">Founding families</h2>
        <p className="mt-1 text-sm text-q-muted" data-testid="admin-founding-counts">{fc.claimed} claimed · {fc.in_trial} in trial · {fc.left} left of {fc.cap}</p>
        <div className="mt-3 flex flex-wrap items-center gap-4">
          <label className="flex min-h-[44px] items-center gap-2 text-sm text-q-text"><input type="checkbox" checked={f.f_on} disabled={!isSuper} onChange={(e) => set(["f_on"], e.target.checked)} data-testid="admin-founding-on" /> Offer on</label>
          <label className="text-xs text-q-muted">Cap {inp(["f_cap"], "admin-founding-cap", "w-24")}</label>
        </div>
      </section>
      {isSuper ? <button className="q-btn q-btn-primary mt-6 min-h-[44px]" disabled={busy} onClick={save} data-testid="admin-plans-save">{busy && <Loader2 className="h-4 w-4 animate-spin" />} Save plans</button>
        : <p className="mt-6 text-sm text-q-muted">Only the super admin can edit plans.</p>}
    </div>
  );
}
